import { useState, useMemo, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Search,
  RefreshCw,
  Download,
  ChevronDown,
  ChevronRight,
  ChevronLeft,
  AlertTriangle,
  ArrowUp,
  ArrowDown,
  User,
  MapPin,
  X,
} from 'lucide-react'
import { MapContainer, TileLayer, Marker, Popup, Polyline, useMap } from 'react-leaflet'
import L from 'leaflet'
import { getRadios, previewRadioSync, executeRadioSync, refreshRadioMetrics, refreshCustomers, getRadioCustomer, getRnBnHistory, isCustomerBlocked, Radio, SyncPreview, RadioMetrics, CustomerData, BnHistoryItem } from '../services/api'
import { formatDistanceToNow } from 'date-fns'
import SyncConfirmDialog from '../components/SyncConfirmDialog'
import LinkHistoryChart from '../components/LinkHistoryChart'
import { useRole } from '../lib/RoleContext'
import { hasRole } from '../lib/role'
import 'leaflet/dist/leaflet.css'

const statusFilters = [
  { value: '', label: 'All' },
  { value: 'connected', label: 'Connected' },
  { value: 'offline', label: 'Offline' },
  { value: 'rogue', label: 'Rogue' },
  { value: 'disabled_channels', label: 'Missing Channels' },
  { value: 'bad_eth', label: 'Bad Ethernet' },
  { value: 'port_errors', label: 'Port Errors' },
  { value: 'mismatched_locations', label: 'Mismatched Locations' },
]

// Haversine formula to calculate distance between two GPS coordinates in meters
function calculateDistanceMeters(
  lat1: number, lon1: number,
  lat2: number, lon2: number
): number {
  const R = 6371000 // Earth's radius in meters
  const dLat = (lat2 - lat1) * Math.PI / 180
  const dLon = (lon2 - lon1) * Math.PI / 180
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLon / 2) * Math.sin(dLon / 2)
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
  return R * c
}

// 50 feet in meters
const GPS_DISCREPANCY_THRESHOLD_METERS = 15.24

const pageSizeOptions = [10, 25, 50, 100]

type SortField = 'serial' | 'hostname' | 'customer' | 'sla' | 'rssi' | 'distance' | 'pathloss'
type SortDirection = 'asc' | 'desc'

interface SortableHeaderProps {
  label: string
  field: SortField
  currentSort: SortField | null
  sortDirection: SortDirection
  onSort: (field: SortField) => void
}

function SortableHeader({ label, field, currentSort, sortDirection, onSort }: SortableHeaderProps) {
  const isActive = currentSort === field
  return (
    <th
      className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
      onClick={() => onSort(field)}
    >
      <div className="flex items-center space-x-1">
        <span>{label}</span>
        {isActive && (
          sortDirection === 'asc' ? (
            <ArrowUp className="h-3 w-3" />
          ) : (
            <ArrowDown className="h-3 w-3" />
          )
        )}
      </div>
    </th>
  )
}

function StatusBadge({ radio }: { radio: Radio }) {
  if (radio.needs_attention) {
    return (
      <span className="px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700">
        Needs Attention
      </span>
    )
  }
  return (
    <span
      className={`px-2 py-1 rounded-full text-xs font-medium ${
        radio.connected ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
      }`}
    >
      {radio.connected ? 'Connected' : 'Offline'}
    </span>
  )
}

interface ExpandedRowProps {
  radio: Radio
  allRadios?: Radio[]
  setCustomerCache: React.Dispatch<React.SetStateAction<Map<string, CustomerData>>>
}

// Custom Leaflet icons
const createCustomIcon = (color: string, hasWarning = false) => {
  const warningBadge = hasWarning ? `
    <div style="
      position: absolute;
      top: -4px;
      right: -4px;
      width: 14px;
      height: 14px;
      background-color: #EF4444;
      border: 2px solid white;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 10px;
      font-weight: bold;
      color: white;
      box-shadow: 0 2px 4px rgba(0,0,0,0.3);
    ">!</div>
  ` : ''

  return L.divIcon({
    className: 'custom-marker',
    html: `
      <div style="position: relative;">
        <div style="background-color: ${color}; width: 24px; height: 24px; border-radius: 50%; border: 3px solid white; box-shadow: 0 2px 6px rgba(0,0,0,0.4);"></div>
        ${warningBadge}
      </div>
    `,
    iconSize: [32, 32],
    iconAnchor: [16, 16],
  })
}

const createRadioIcon = (hasDiscrepancy: boolean) => createCustomIcon('#3B82F6', hasDiscrepancy) // blue
const customerIcon = createCustomIcon('#10B981', false) // green

// Component to fit map bounds to show both markers
function FitMapBounds({ bounds }: { bounds: [[number, number], [number, number]] }) {
  const map = useMap()
  useEffect(() => {
    map.fitBounds(bounds, { padding: [50, 50] })
  }, [map, bounds])
  return null
}

function ExpandedRow({ radio, allRadios, setCustomerCache }: ExpandedRowProps) {
  // Customer data state
  const [customerData, setCustomerData] = useState<CustomerData | null>(null)
  const [isLoadingCustomer, setIsLoadingCustomer] = useState(false)
  const [customerError, setCustomerError] = useState<string | null>(null)

  // Mini map state
  const [showMiniMap, setShowMiniMap] = useState(false)

  // BN roam history (RN-only)
  const [bnHistory, setBnHistory] = useState<BnHistoryItem[]>([])

  // Look up BN hostnames from the radios list
  const getBnHostname = (bnSerial: string | null): string | null => {
    if (!bnSerial || !allRadios) return null
    const bn = allRadios.find(r => r.serial_number === bnSerial)
    return bn?.hostname || null
  }

  const connectedBnHostname = getBnHostname(radio.connected_bn)

  // Fetch customer data on mount (only for RNs) - always refresh, never use cache
  useEffect(() => {
    // Skip for BNs - they don't have customers
    if (radio.radio_type === 'BN') return

    // Always fetch fresh data when row is expanded
    const fetchCustomerData = async () => {
      setIsLoadingCustomer(true)
      setCustomerError(null)

      try {
        const response = await getRadioCustomer(radio.serial_number)
        setCustomerData(response.data)
        // Update client-side cache for display in table rows
        setCustomerCache(prev => new Map(prev).set(radio.serial_number, response.data))
      } catch (err) {
        setCustomerError('Failed to load customer data')
        console.error('Error fetching customer data:', err)
      } finally {
        setIsLoadingCustomer(false)
      }
    }

    fetchCustomerData()
  }, [radio.serial_number, radio.radio_type, setCustomerCache])

  // Fetch BN roam history (only for RNs)
  useEffect(() => {
    if (radio.radio_type === 'BN') return
    getRnBnHistory(radio.serial_number)
      .then(r => setBnHistory(r.data.items))
      .catch(() => setBnHistory([]))
  }, [radio.serial_number, radio.radio_type])

  return (
    <tr className="bg-gray-50">
      <td colSpan={11} className="px-4 py-4">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div>
            <span className="text-gray-500">Hostname:</span>
            <span className="ml-2 font-medium">{radio.hostname || radio.serial_number}</span>
          </div>
          <div>
            <span className="text-gray-500">Type:</span>
            <span className="ml-2 font-medium">
              {radio.radio_type === 'BN' ? 'Base Node' : 'Remote Node'}
            </span>
          </div>
          <div>
            <span className="text-gray-500">Frequency:</span>
            {radio.radio_type === 'BN' && radio.carriers && radio.carriers.length > 0 ? (
              <span className={`ml-2 font-medium ${radio.frequency_band?.includes('6') ? 'text-cyan-600' : 'text-green-600'}`}>
                {radio.carrier_count} carrier{radio.carrier_count !== 1 ? 's' : ''}: {radio.carriers.map((c, i) => (
                  <span key={i}>
                    {c.frequency_ghz?.toFixed(3)} GHz
                    {c.bandwidth_mhz && <span className="text-gray-400 text-xs ml-0.5">({c.bandwidth_mhz} MHz)</span>}
                    {i < radio.carriers!.length - 1 && ', '}
                  </span>
                ))}
              </span>
            ) : (
              <span className={`ml-2 font-medium ${radio.frequency_band?.includes('6') ? 'text-cyan-600' : 'text-green-600'}`}>
                {radio.frequency_band || '-'}
              </span>
            )}
          </div>
          <div>
            <span className="text-gray-500">Market:</span>
            <span className="ml-2 font-medium">{radio.market || '-'}</span>
          </div>
          <div>
            <span className="text-gray-500">Operator:</span>
            <span className="ml-2 font-medium">{radio.operator || '-'}</span>
          </div>
          {radio.radio_type === 'RN' && (
            <div>
              <span className="text-gray-500">Connected BN:</span>
              <span className="ml-2">
                {radio.connected_bn ? (
                  <>
                    <span className="font-medium">{connectedBnHostname || radio.connected_bn}</span>
                    {connectedBnHostname && (
                      <span className="text-gray-400 text-xs ml-1">({radio.connected_bn})</span>
                    )}
                  </>
                ) : '-'}
              </span>
            </div>
          )}
          {radio.radio_type === 'RN' && (
            <div>
              <span className="text-gray-500">Home BN:</span>
              <span className="ml-2">
                {radio.primary_bn ? (
                  <>
                    <span className="font-medium">{getBnHostname(radio.primary_bn) || radio.primary_bn}</span>
                    {radio.connected && radio.connected_bn && radio.primary_bn !== radio.connected_bn && (
                      <span className="ml-2 px-1.5 py-0.5 rounded text-xs bg-yellow-100 text-yellow-700" title="Connected to a non-home BN">roaming</span>
                    )}
                  </>
                ) : <span className="text-gray-400">-</span>}
              </span>
            </div>
          )}
          {radio.radio_type !== 'BN' && bnHistory.length > 0 && (
            <div className="col-span-2 md:col-span-4">
              <span className="text-gray-500">Recent BN changes:</span>
              <div className="mt-1 space-y-1">
                {bnHistory.slice(0, 5).map((h, i) => (
                  <div key={i} className="text-xs font-mono">
                    {formatDistanceToNow(new Date(h.changed_at), { addSuffix: true })}:
                    {' '}{getBnHostname(h.from_bn) || h.from_bn || '?'} → {getBnHostname(h.to_bn) || h.to_bn}
                    {h.to_is_home
                      ? <span className="ml-2 text-green-600">home</span>
                      : <span className="ml-2 text-yellow-600">non-home</span>}
                    {h.reason && <span className="ml-2 text-gray-400">({h.reason})</span>}
                  </div>
                ))}
              </div>
            </div>
          )}
          {radio.radio_type === 'BN' && (
            <div>
              <span className="text-gray-500">Azimuth:</span>
              <span className="ml-2 font-medium">{radio.azimuth}°</span>
            </div>
          )}
          {radio.radio_type === 'BN' && radio.mgmt_ip && (
            <div>
              <span className="text-gray-500">Management IP:</span>
              <span className="ml-2 font-mono">{radio.mgmt_ip}</span>
            </div>
          )}
          {radio.radio_type === 'BN' && radio.active_connections != null && (
            <div>
              <span className="text-gray-500">Connected RNs:</span>
              <span className="ml-2 font-medium">{radio.active_connections}</span>
            </div>
          )}
          {radio.radio_type === 'BN' && (radio.dl_subscriber_rate_kbps != null || radio.ul_subscriber_rate_kbps != null) && (
            <div>
              <span className="text-gray-500">Subscriber rate:</span>
              <span className="ml-2 font-medium">
                {((radio.dl_subscriber_rate_kbps ?? 0) / 1000).toFixed(1)} /{' '}
                {((radio.ul_subscriber_rate_kbps ?? 0) / 1000).toFixed(1)} Mbps
              </span>
            </div>
          )}
          {radio.radio_type === 'BN' && radio.resource_utilization_pct != null && (
            <div>
              <span className="text-gray-500">Utilization:</span>
              <span className="ml-2 font-medium">{radio.resource_utilization_pct.toFixed(1)}%</span>
            </div>
          )}
          {radio.radio_type === 'RN' && radio.eth_port_up != null && (
            <div>
              <span className="text-gray-500">Subscriber Port:</span>
              <span className="ml-2 font-medium">
                {radio.eth_port_up ? (
                  <span className="text-green-600">
                    Up{radio.eth_port_speed ? ` · ${radio.eth_port_speed.replace('SPEED_', '').replace('GB', 'G')}` : ''}
                  </span>
                ) : (
                  <span className="text-red-600">Down</span>
                )}
                {((radio.eth_in_fcs_errors ?? 0) + (radio.eth_in_errors ?? 0) + (radio.eth_out_errors ?? 0)) > 0 && (
                  <span className="text-amber-600 text-xs ml-1" title="Ethernet errors/FCS — possible bad cable">⚠ errors</span>
                )}
              </span>
            </div>
          )}
          {radio.radio_type === 'RN' && radio.eth_port_up != null && (
            <div>
              <span className="text-gray-500">Hosts:</span>
              <span className="ml-2 font-medium">
                {radio.host_count ?? 0}
                {radio.host_macs && radio.host_macs.length > 0 && (
                  <span className="text-gray-400 text-xs ml-1 font-mono">
                    ({radio.host_macs.map((h) => h.mac).join(', ')})
                  </span>
                )}
              </span>
            </div>
          )}
          <div>
            <span className="text-gray-500">Radio GPS:</span>
            {(() => {
              if (!radio.latitude || !radio.longitude) {
                return <span className="ml-2 font-mono text-xs">-</span>
              }
              // Check distance from customer GPS if available (RNs only)
              const hasCustomerGps = customerData?.customer_latitude && customerData?.customer_longitude
              let gpsDistance: number | null = null
              let isDiscrepancy = false

              if (radio.radio_type === 'RN' && hasCustomerGps) {
                gpsDistance = calculateDistanceMeters(
                  radio.latitude, radio.longitude,
                  customerData.customer_latitude!, customerData.customer_longitude!
                )
                isDiscrepancy = gpsDistance > GPS_DISCREPANCY_THRESHOLD_METERS
              }

              return (
                <span className={`ml-2 font-mono text-xs ${isDiscrepancy ? 'text-red-600 font-semibold' : ''}`}>
                  {radio.latitude.toFixed(5)}, {radio.longitude.toFixed(5)}
                  {isDiscrepancy && gpsDistance && (
                    <span className="ml-2 text-red-600">
                      ({gpsDistance.toFixed(1)}m from customer)
                    </span>
                  )}
                </span>
              )
            })()}
          </div>
          <div>
            <span className="text-gray-500">Last Seen:</span>
            <span className="ml-2">
              {radio.last_seen_at
                ? formatDistanceToNow(new Date(radio.last_seen_at), { addSuffix: true })
                : 'Never'}
            </span>
          </div>
          {radio.distance !== null && radio.distance !== undefined && (
            <div>
              <span className="text-gray-500">Distance:</span>
              <span className="ml-2 font-mono">
                {(radio.distance / 1000).toFixed(2)} km
              </span>
            </div>
          )}
          {radio.pathloss !== null && radio.pathloss !== undefined && (
            <div>
              <span className="text-gray-500">Path Loss:</span>
              <span className={`ml-2 font-mono ${radio.pathloss > 140 ? 'text-red-600 font-semibold' : ''}`}>
                {radio.pathloss.toFixed(1)} dB
              </span>
            </div>
          )}
          {radio.alignment_metric !== null && radio.alignment_metric !== undefined && (
            <div>
              <span className="text-gray-500">Alignment:</span>
              <span className="ml-2 font-mono">
                {radio.alignment_metric.toFixed(1)} dB
              </span>
            </div>
          )}
          {(radio.dl_capacity_kbps != null || radio.ul_capacity_kbps != null) && (
            <div>
              <span className="text-gray-500">Link capacity:</span>
              <span className="ml-2 font-mono">
                {((radio.dl_capacity_kbps ?? 0) / 1000).toFixed(1)}
                {' / '}
                {((radio.ul_capacity_kbps ?? 0) / 1000).toFixed(1)} Mbps
              </span>
            </div>
          )}
          {radio.eth_errors_1h != null && (
            <div>
              <span className="text-gray-500">Port errors (1h):</span>
              <span className={`ml-2 font-mono ${radio.eth_errors_1h > 0 ? 'text-red-600 font-semibold' : ''}`}>
                {radio.eth_errors_1h}
              </span>
            </div>
          )}
          {radio.channels && radio.channels.length > 0 && (
            <div className="col-span-2 md:col-span-4">
              <span className="text-gray-500">Channels:</span>
              {radio.disabled_6ghz_count ? (
                <span className="ml-2 text-red-600 font-semibold">
                  {radio.disabled_6ghz_count} disabled 6 GHz channel{radio.disabled_6ghz_count > 1 ? 's' : ''}
                </span>
              ) : (
                <span className="ml-2 text-green-600">all enabled</span>
              )}
              <div className="mt-1 flex flex-wrap gap-2">
                {radio.channels.map((c) => {
                  const ghz = (c.frequency / 1_000_000).toFixed(3)
                  const disabled6 = c.enabled === false && c.frequency >= 5_925_000
                  return (
                    <span
                      key={c.id}
                      className={`px-2 py-0.5 rounded text-xs font-mono ${
                        disabled6
                          ? 'bg-red-100 text-red-700'
                          : c.enabled
                          ? 'bg-green-100 text-green-700'
                          : 'bg-gray-100 text-gray-500'
                      }`}
                    >
                      {ghz} GHz {c.enabled ? `(${c.width})` : '✕'}
                    </span>
                  )
                })}
              </div>
            </div>
          )}
        </div>

        {radio.radio_type === 'RN' && (
          <LinkHistoryChart serial={radio.serial_number} />
        )}

        {/* Customer Information Section (RNs only) */}
        {radio.radio_type === 'RN' && (
          <div className="mt-4 pt-4 border-t border-gray-200">
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-sm font-semibold text-gray-700 flex items-center">
                <User className="h-4 w-4 mr-1" />
                Splynx Customer
                {customerData?.cache_hit && customerData?.splynx_last_fetched_at && (
                  <span className="ml-2 text-xs font-normal text-gray-400">
                    (cached {formatDistanceToNow(new Date(customerData.splynx_last_fetched_at), { addSuffix: true })})
                  </span>
                )}
              </h4>
              {customerData && !isLoadingCustomer && (
                <button
                  onClick={async () => {
                    setIsLoadingCustomer(true)
                    setCustomerError(null)
                    try {
                      const response = await getRadioCustomer(radio.serial_number, true) // force_refresh=true
                      setCustomerData(response.data)
                      setCustomerCache(prev => new Map(prev).set(radio.serial_number, response.data))
                    } catch (err) {
                      setCustomerError('Failed to refresh customer data')
                      console.error('Error refreshing customer data:', err)
                    } finally {
                      setIsLoadingCustomer(false)
                    }
                  }}
                  className="text-xs text-primary-600 hover:text-primary-800 inline-flex items-center"
                  title="Force refresh from Splynx (bypasses cache)"
                >
                  <RefreshCw className="h-3 w-3 mr-1" />
                  Force Refresh
                </button>
              )}
            </div>

            {isLoadingCustomer && (
              <div className="flex items-center text-gray-500">
                <RefreshCw className="h-4 w-4 animate-spin mr-2" />
                Loading customer data...
              </div>
            )}

            {customerError && (
              <div className="text-red-600 text-sm">{customerError}</div>
            )}

            {customerData && !isLoadingCustomer && (
              customerData.customer_found ? (
                <>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
                    <div>
                      <span className="text-gray-500">Customer ID:</span>
                      <span className="ml-2 font-medium">{customerData.customer_id}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">Customer:</span>
                      <span className="ml-2 font-medium">{customerData.customer_name}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">Plan:</span>
                      <span className="ml-2 font-medium">{customerData.service_name || '-'}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">SLA Profile:</span>
                      <span className="ml-2 font-medium">{customerData.sla_profile || '-'}</span>
                    </div>
                    {customerData.customer_latitude && customerData.customer_longitude && (
                      <div className="col-span-2">
                        <span className="text-gray-500">Customer Location:</span>
                        <span className="ml-2 font-mono text-xs">
                          {customerData.customer_latitude.toFixed(5)}, {customerData.customer_longitude.toFixed(5)}
                        </span>
                      </div>
                    )}
                  </div>
                  {/* Map button - show if both radio and customer have GPS */}
                  {radio.latitude && radio.longitude && customerData.customer_latitude && customerData.customer_longitude && (
                    <button
                      onClick={() => setShowMiniMap(true)}
                      className="mt-3 px-3 py-1.5 bg-primary-600 text-white rounded hover:bg-primary-700 inline-flex items-center text-sm"
                    >
                      <MapPin className="h-4 w-4 mr-1" />
                      View Location Comparison
                    </button>
                  )}
                </>
              ) : (
                <div className="text-sm">
                  {customerData.is_rogue ? (
                    <span className="text-yellow-600 flex items-center">
                      <AlertTriangle className="h-4 w-4 mr-1" />
                      Rogue device - connected but not assigned to customer in Splynx
                    </span>
                  ) : customerData.error ? (
                    <span className="text-gray-500">{customerData.error}</span>
                  ) : (
                    <span className="text-gray-500">
                      Not found in Splynx inventory
                    </span>
                  )}
                </div>
              )
            )}
          </div>
        )}

        {/* Mini Map Modal */}
        {showMiniMap && customerData?.customer_latitude && customerData?.customer_longitude && radio.latitude && radio.longitude && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => setShowMiniMap(false)}>
            <div className="bg-white rounded-lg shadow-xl w-11/12 max-w-4xl flex flex-col" style={{ height: '80vh' }} onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between p-4 border-b">
                <h3 className="text-lg font-semibold">Location Comparison</h3>
                <button
                  onClick={() => setShowMiniMap(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              <div className="p-4 space-y-2 text-sm border-b">
                <div className="flex items-center">
                  <div className="w-6 h-6 bg-blue-500 rounded-full mr-2 border-2 border-white shadow"></div>
                  <span className="font-medium">Radio Location: {radio.latitude.toFixed(5)}, {radio.longitude.toFixed(5)}</span>
                </div>
                <div className="flex items-center">
                  <div className="w-6 h-6 bg-green-500 rounded-full mr-2 border-2 border-white shadow"></div>
                  <span className="font-medium">Customer Location: {customerData.customer_latitude.toFixed(5)}, {customerData.customer_longitude.toFixed(5)}</span>
                </div>
                {(() => {
                  const distance = calculateDistanceMeters(
                    radio.latitude, radio.longitude,
                    customerData.customer_latitude, customerData.customer_longitude
                  )
                  return (
                    <div className={`font-semibold ${distance > GPS_DISCREPANCY_THRESHOLD_METERS ? 'text-red-600' : 'text-green-600'}`}>
                      Distance: {distance.toFixed(1)} meters ({(distance * 3.28084).toFixed(1)} feet)
                    </div>
                  )
                })()}
              </div>
              <div className="flex-1 relative">
                <MapContainer
                  center={[
                    (radio.latitude + customerData.customer_latitude) / 2,
                    (radio.longitude + customerData.customer_longitude) / 2
                  ]}
                  zoom={13}
                  style={{ height: '100%', width: '100%' }}
                >
                  <TileLayer
                    url="https://{s}.google.com/vt/lyrs=y&x={x}&y={y}&z={z}"
                    subdomains={['mt0', 'mt1', 'mt2', 'mt3']}
                    attribution="&copy; Google Maps"
                  />
                  {/* Auto-fit bounds to show both markers */}
                  <FitMapBounds bounds={[
                    [radio.latitude, radio.longitude],
                    [customerData.customer_latitude, customerData.customer_longitude]
                  ]} />
                  {/* Radio location - blue marker with warning if discrepancy */}
                  <Marker position={[radio.latitude, radio.longitude]} icon={(() => {
                    const distance = calculateDistanceMeters(
                      radio.latitude, radio.longitude,
                      customerData.customer_latitude, customerData.customer_longitude
                    )
                    return createRadioIcon(distance > GPS_DISCREPANCY_THRESHOLD_METERS)
                  })()}>
                    <Popup>
                      <div className="text-sm">
                        <strong>Radio</strong><br />
                        {radio.hostname || radio.serial_number}<br />
                        {radio.latitude.toFixed(5)}, {radio.longitude.toFixed(5)}
                      </div>
                    </Popup>
                  </Marker>
                  {/* Customer location - green marker */}
                  <Marker position={[customerData.customer_latitude, customerData.customer_longitude]} icon={customerIcon}>
                    <Popup>
                      <div className="text-sm">
                        <strong>Customer</strong><br />
                        {customerData.customer_name}<br />
                        {customerData.customer_latitude.toFixed(5)}, {customerData.customer_longitude.toFixed(5)}
                      </div>
                    </Popup>
                  </Marker>
                  {/* Bold red line connecting them */}
                  <Polyline
                    positions={[
                      [radio.latitude, radio.longitude],
                      [customerData.customer_latitude, customerData.customer_longitude]
                    ]}
                    color="#DC2626"
                    weight={6}
                    opacity={0.8}
                    dashArray="10, 10"
                  />
                </MapContainer>
              </div>
            </div>
          </div>
        )}
      </td>
    </tr>
  )
}

export default function Radios() {
  const role = useRole()
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [hideBN, setHideBN] = useState(false)
  const [selectedRadios, setSelectedRadios] = useState<string[]>([])
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set())
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(25)

  // Sorting state
  const [sortField, setSortField] = useState<SortField | null>(null)
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc')

  // Sync confirmation dialog state
  const [syncDialogOpen, setSyncDialogOpen] = useState(false)
  const [syncPreview, setSyncPreview] = useState<SyncPreview | null>(null)
  const [syncPreviewLoading, setSyncPreviewLoading] = useState(false)
  const [currentSyncSerial, setCurrentSyncSerial] = useState<string | null>(null)
  const [syncError, setSyncError] = useState<string | null>(null)

  // Metrics refresh state
  const [metricsCache, setMetricsCache] = useState<Map<string, RadioMetrics>>(new Map())

  // Customer refresh message
  const [refreshMsg, setRefreshMsg] = useState<string | null>(null)

  // Customer data cache (persists across expand/collapse)
  const [customerCache, setCustomerCache] = useState<Map<string, CustomerData>>(new Map())

  // Customer loading progress for auto-fetch
  const [customerLoadingProgress, setCustomerLoadingProgress] = useState<{
    loading: boolean
    loaded: number
    total: number
  }>({ loading: false, loaded: 0, total: 0 })

  const queryClient = useQueryClient()

  const { data, isLoading, error } = useQuery({
    queryKey: ['radios'],
    queryFn: async () => {
      const response = await getRadios({})
      return response.data
    },
  })

  // Filter and sort data on frontend for instant search
  const filteredData = useMemo(() => {
    if (!data?.items) return []

    // Merge metrics cache with radio data
    let items = data.items.map(radio => {
      const cachedMetrics = metricsCache.get(radio.serial_number)
      if (cachedMetrics) {
        return {
          ...radio,
          rssi: cachedMetrics.rssi,
          distance: cachedMetrics.distance,
          pathloss: cachedMetrics.pathloss,
        }
      }
      return radio
    })

    // Apply status filter
    if (statusFilter === 'connected') {
      items = items.filter(r => r.connected)
    } else if (statusFilter === 'offline') {
      items = items.filter(r => !r.connected)
    } else if (statusFilter === 'rogue') {
      items = items.filter(r => r.is_rogue)
    } else if (statusFilter === 'disabled_channels') {
      // Radios not getting all their channels: at least one disabled 6 GHz carrier
      items = items.filter(r => (r.disabled_6ghz_count ?? 0) > 0)
    } else if (statusFilter === 'bad_eth') {
      // Online radios whose subscriber port is known-not-1G-full-duplex-up
      // (down / wrong speed / half-duplex). Unknown/null values are not flagged.
      items = items.filter(r =>
        r.connected && (
          r.eth_port_up === false ||
          (r.eth_port_speed != null && r.eth_port_speed !== 'SPEED_1GB') ||
          (r.eth_port_duplex != null && r.eth_port_duplex !== 'full')
        )
      )
    } else if (statusFilter === 'port_errors') {
      // Online radios whose gi1 port took errors in the last hour (windowed rate)
      items = items.filter(r => r.connected && (r.eth_errors_1h ?? 0) > 0)
    } else if (statusFilter === 'mismatched_locations') {
      // Connected RNs whose radio GPS is > 50 ft from the Splynx customer pin
      // (same as the yellow-row GPS-discrepancy flag). Customer coords ride on the
      // row payload (fleet-wide), NOT the page-scoped customerCache.
      items = items.filter(r => {
        if (!r.connected || r.radio_type !== 'RN') return false
        if (r.customer_latitude == null || r.customer_longitude == null || r.latitude == null || r.longitude == null) return false
        return calculateDistanceMeters(r.latitude, r.longitude, r.customer_latitude, r.customer_longitude) > GPS_DISCREPANCY_THRESHOLD_METERS
      })
    }

    // Apply BN filter
    if (hideBN) {
      items = items.filter(r => r.radio_type !== 'BN')
    }

    // Apply search filter
    if (search) {
      const searchLower = search.toLowerCase()
      items = items.filter(r =>
        r.serial_number.toLowerCase().includes(searchLower) ||
        (r.hostname || '').toLowerCase().includes(searchLower) ||
        (r.customer_name || '').toLowerCase().includes(searchLower)
      )
    }

    // Apply sorting
    if (sortField) {
      items = [...items].sort((a, b) => {
        let aVal: any
        let bVal: any

        switch (sortField) {
          case 'serial':
            aVal = a.serial_number
            bVal = b.serial_number
            break
          case 'hostname':
            aVal = a.hostname || ''
            bVal = b.hostname || ''
            break
          case 'customer':
            aVal = a.customer_name || ''
            bVal = b.customer_name || ''
            break
          case 'sla':
            aVal = a.sla_profile || ''
            bVal = b.sla_profile || ''
            break
          case 'rssi':
            aVal = a.rssi ?? -999
            bVal = b.rssi ?? -999
            break
          case 'distance':
            aVal = a.distance ?? -999
            bVal = b.distance ?? -999
            break
          case 'pathloss':
            aVal = a.pathloss ?? -999
            bVal = b.pathloss ?? -999
            break
          default:
            return 0
        }

        if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1
        if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1
        return 0
      })
    }

    return items
  }, [data?.items, statusFilter, hideBN, search, sortField, sortDirection, metricsCache, customerCache])

  const syncMutation = useMutation({
    mutationFn: (serial: string) => executeRadioSync(serial),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['radios'] })
      queryClient.invalidateQueries({ queryKey: ['dashboardStats'] })
      setSyncDialogOpen(false)
      setSyncPreview(null)
      setCurrentSyncSerial(null)
      setSyncError(null)
    },
    onError: (error: any) => {
      // Don't hang the dialog on failure — surface the backend error (FastAPI
      // returns it as `detail`) and let the user retry or close.
      const detail =
        error?.response?.data?.detail ?? error?.message ?? 'Sync failed. Please try again.'
      setSyncError(typeof detail === 'string' ? detail : JSON.stringify(detail))
    },
  })

  const refreshMetricsMutation = useMutation({
    mutationFn: (serialNumbers: string[]) => refreshRadioMetrics(serialNumbers),
    onSuccess: (response) => {
      // Update metrics cache
      const newCache = new Map(metricsCache)
      response.data.metrics.forEach(metric => {
        newCache.set(metric.serial_number, metric)
      })
      setMetricsCache(newCache)
    },
  })

  const refreshCustomersMutation = useMutation({
    mutationFn: () => refreshCustomers(),
    onSuccess: (res) => {
      const d = res.data
      setRefreshMsg(`Refreshed ${d.total}: ${d.enriched} assigned, ${d.rogue} rogue, ${d.errors} errors`)
      queryClient.invalidateQueries({ queryKey: ['radios'] })
      queryClient.invalidateQueries({ queryKey: ['dashboardStats'] })
    },
    onError: (err: any) => {
      setRefreshMsg(err?.response?.data?.detail ?? 'Customer refresh failed')
    },
  })

  const handleSyncClick = async (serial: string) => {
    setCurrentSyncSerial(serial)
    setSyncError(null)
    setSyncDialogOpen(true)
    setSyncPreviewLoading(true)

    try {
      const response = await previewRadioSync(serial)
      setSyncPreview(response.data)
    } catch (error) {
      console.error('Error previewing sync:', error)
      setSyncPreview({
        serial_number: serial,
        radio_exists: false,
        customer_found: false,
        changes_detected: false,
        current_hostname: null,
        current_sla: null,
        current_latitude: null,
        current_longitude: null,
        proposed_hostname: null,
        proposed_sla: null,
        proposed_latitude: null,
        proposed_longitude: null,
        customer_id: null,
        customer_name: null,
        service_name: null,
        error: 'Failed to load preview'
      })
    } finally {
      setSyncPreviewLoading(false)
    }
  }

  const handleSyncConfirm = () => {
    if (currentSyncSerial) {
      setSyncError(null)
      syncMutation.mutate(currentSyncSerial)
    }
  }

  const handleSyncCancel = () => {
    setSyncDialogOpen(false)
    setSyncPreview(null)
    setCurrentSyncSerial(null)
    setSyncError(null)
  }

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      // Toggle direction if same field
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
    } else {
      // New field, default to ascending
      setSortField(field)
      setSortDirection('asc')
    }
  }

  // Pagination calculations
  const totalItems = filteredData.length
  const totalPages = Math.ceil(totalItems / pageSize) || 1

  const paginatedItems = useMemo(() => {
    const start = (currentPage - 1) * pageSize
    const end = start + pageSize
    return filteredData.slice(start, end)
  }, [filteredData, currentPage, pageSize])

  // Auto-fetch customer data for all visible RNs on page
  useEffect(() => {
    const fetchCustomerDataForPage = async () => {
      // Get RNs on current page that aren't in cache
      const rnsToFetch = paginatedItems.filter(
        r => r.radio_type === 'RN' && !customerCache.has(r.serial_number)
      )

      if (rnsToFetch.length === 0) return

      setCustomerLoadingProgress({
        loading: true,
        loaded: 0,
        total: rnsToFetch.length
      })

      // Fetch with throttling (100ms delay between requests)
      for (let i = 0; i < rnsToFetch.length; i++) {
        const radio = rnsToFetch[i]

        try {
          const response = await getRadioCustomer(radio.serial_number)
          setCustomerCache(prev => new Map(prev).set(radio.serial_number, response.data))
        } catch (err) {
          // Store error state in cache to avoid retrying
          setCustomerCache(prev => new Map(prev).set(radio.serial_number, {
            serial_number: radio.serial_number,
            customer_found: false,
            customer_id: null,
            customer_name: null,
            customer_latitude: null,
            customer_longitude: null,
            service_name: null,
            tariff_id: null,
            sla_profile: null,
            splynx_last_fetched_at: null,
            cache_hit: false,
            is_rogue: false,
            error: 'Failed to fetch'
          }))
        }

        setCustomerLoadingProgress(prev => ({
          ...prev,
          loaded: i + 1
        }))

        // Throttle: 100ms delay between requests
        if (i < rnsToFetch.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 100))
        }
      }

      setCustomerLoadingProgress({ loading: false, loaded: 0, total: 0 })
    }

    fetchCustomerDataForPage()
  }, [paginatedItems]) // Re-run when page changes

  // Reset to page 1 when filters change
  const handleSearchChange = (value: string) => {
    setSearch(value)
    setCurrentPage(1)
  }

  const handleStatusFilterChange = (value: string) => {
    setStatusFilter(value)
    setCurrentPage(1)
  }

  const handlePageSizeChange = (size: number) => {
    setPageSize(size)
    setCurrentPage(1)
  }

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedRadios(paginatedItems.map((r) => r.serial_number))
    } else {
      setSelectedRadios([])
    }
  }

  const handleSelectRadio = (serial: string, checked: boolean) => {
    if (checked) {
      setSelectedRadios([...selectedRadios, serial])
    } else {
      setSelectedRadios(selectedRadios.filter((s) => s !== serial))
    }
  }

  const toggleExpanded = (serial: string) => {
    setExpandedRows(prev => {
      const next = new Set(prev)
      if (next.has(serial)) {
        next.delete(serial)
      } else {
        next.add(serial)
      }
      return next
    })
  }

  const handleExportCSV = () => {
    if (!data?.items) return

    const headers = [
      'Serial',
      'Hostname',
      'Customer',
      'SLA',
      'Status',
      'Connected',
      'Last Sync',
    ]
    const rows = data.items.map((r) => [
      r.serial_number,
      r.hostname || '',
      r.customer_name || '',
      r.sla_profile || '',
      r.connected ? 'Connected' : 'Offline',
      r.connected ? 'Yes' : 'No',
      r.last_synced_at || '',
    ])

    const csv = [headers, ...rows].map((row) => row.join(',')).join('\n')
    const blob = new Blob([csv], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'tarana-radios.csv'
    a.click()
  }

  // Generate page numbers to display
  const getPageNumbers = () => {
    const pages: (number | string)[] = []
    const maxVisible = 5

    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) pages.push(i)
    } else {
      if (currentPage <= 3) {
        for (let i = 1; i <= 4; i++) pages.push(i)
        pages.push('...')
        pages.push(totalPages)
      } else if (currentPage >= totalPages - 2) {
        pages.push(1)
        pages.push('...')
        for (let i = totalPages - 3; i <= totalPages; i++) pages.push(i)
      } else {
        pages.push(1)
        pages.push('...')
        for (let i = currentPage - 1; i <= currentPage + 1; i++) pages.push(i)
        pages.push('...')
        pages.push(totalPages)
      }
    }
    return pages
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 text-gray-400 animate-spin" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-red-800">Failed to load radios</p>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-900">Radios</h1>
        <div className="flex space-x-2">
          {hasRole(role, 'operator') && (
            <button
              onClick={() => {
                const serials = paginatedItems.map(r => r.serial_number)
                refreshMetricsMutation.mutate(serials)
              }}
              disabled={refreshMetricsMutation.isPending || paginatedItems.length === 0}
              className="flex items-center px-3 py-2 border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              title="Fetch detailed signal metrics for visible radios"
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${refreshMetricsMutation.isPending ? 'animate-spin' : ''}`} />
              {refreshMetricsMutation.isPending ? 'Refreshing...' : 'Refresh Metrics'}
            </button>
          )}
          <button
            onClick={handleExportCSV}
            className="flex items-center px-3 py-2 border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50"
          >
            <Download className="h-4 w-4 mr-2" />
            Export CSV
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="flex flex-wrap gap-4">
          <div className="flex-1 min-w-[200px]">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search by serial, hostname, or customer..."
                value={search}
                onChange={(e) => handleSearchChange(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>
          </div>
          <div className="flex space-x-2">
            {statusFilters.map((filter) => (
              <button
                key={filter.value}
                onClick={() => handleStatusFilterChange(filter.value)}
                className={`px-4 py-2 rounded-md text-sm font-medium ${
                  statusFilter === filter.value
                    ? 'bg-primary-100 text-primary-700'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {filter.label}
              </button>
            ))}
            {hasRole(role, 'operator') && (
              <button
                onClick={() => refreshCustomersMutation.mutate()}
                disabled={refreshCustomersMutation.isPending}
                className="px-3 py-1 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {refreshCustomersMutation.isPending ? 'Refreshing…' : 'Refresh customer data'}
              </button>
            )}
            {refreshMsg && <span className="ml-2 text-sm text-gray-600">{refreshMsg}</span>}
          </div>
          <div className="flex items-center ml-4 border-l pl-4 border-gray-300">
            <label className="flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={hideBN}
                onChange={(e) => setHideBN(e.target.checked)}
                className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              />
              <span className="ml-2 text-sm text-gray-700">Hide Base Nodes</span>
            </label>
          </div>
        </div>
      </div>

      {/* Stats Bar */}
      <div className="flex space-x-4 text-sm">
        <span className="text-gray-600">
          Total: <strong>{filteredData.length}</strong>
          {search && <span className="text-gray-400 ml-1">(filtered from {data?.items.length || 0})</span>}
        </span>
        <span className="text-green-600">
          Connected: <strong>{filteredData.filter(r => r.connected).length}</strong>
        </span>
        <span className="text-red-600">
          Offline: <strong>{filteredData.filter(r => !r.connected).length}</strong>
        </span>
        <span className="text-yellow-600">
          Needs Attention: <strong>{filteredData.filter(r => r.needs_attention).length}</strong>
        </span>

        {/* Loading progress for Splynx customer data */}
        {customerLoadingProgress.loading && (
          <span className="text-blue-600 flex items-center">
            <RefreshCw className="h-3 w-3 animate-spin mr-1" />
            Loading customer data: {customerLoadingProgress.loaded}/{customerLoadingProgress.total}
          </span>
        )}
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-2 py-3 text-left w-8"></th>
              <th className="px-4 py-3 text-left">
                <input
                  type="checkbox"
                  checked={
                    selectedRadios.length === paginatedItems.length &&
                    paginatedItems.length > 0
                  }
                  onChange={(e) => handleSelectAll(e.target.checked)}
                  className="rounded border-gray-300"
                />
              </th>
              <SortableHeader
                label="Serial"
                field="serial"
                currentSort={sortField}
                sortDirection={sortDirection}
                onSort={handleSort}
              />
              <SortableHeader
                label="Hostname"
                field="hostname"
                currentSort={sortField}
                sortDirection={sortDirection}
                onSort={handleSort}
              />
              <SortableHeader
                label="Customer"
                field="customer"
                currentSort={sortField}
                sortDirection={sortDirection}
                onSort={handleSort}
              />
              <SortableHeader
                label="SLA"
                field="sla"
                currentSort={sortField}
                sortDirection={sortDirection}
                onSort={handleSort}
              />
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <SortableHeader
                label="RSSI"
                field="rssi"
                currentSort={sortField}
                sortDirection={sortDirection}
                onSort={handleSort}
              />
              <SortableHeader
                label="Distance"
                field="distance"
                currentSort={sortField}
                sortDirection={sortDirection}
                onSort={handleSort}
              />
              <SortableHeader
                label="Pathloss"
                field="pathloss"
                currentSort={sortField}
                sortDirection={sortDirection}
                onSort={handleSort}
              />
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {paginatedItems.map((radio) => {
              // Determine row background color based on customer data status
              const cached = customerCache.get(radio.serial_number)
              let rowBgClass = ''

              // Highest precedence: Splynx account blocked/disabled → dark red.
              if (isCustomerBlocked(radio.customer_status)) {
                rowBgClass = 'bg-red-800 text-white'
              }

              if (!rowBgClass && radio.radio_type === 'RN' && cached) {
                if (!cached.customer_found) {
                  // Red: No customer found (rogue or not in Splynx)
                  rowBgClass = 'bg-red-200'
                } else if (cached.customer_latitude && cached.customer_longitude &&
                           radio.latitude && radio.longitude) {
                  // Check if GPS needs correction (> 50 feet = 15.24m)
                  const gpsDistance = calculateDistanceMeters(
                    radio.latitude, radio.longitude,
                    cached.customer_latitude, cached.customer_longitude
                  )
                  if (gpsDistance > GPS_DISCREPANCY_THRESHOLD_METERS) {
                    // Yellow: Location needs correction
                    rowBgClass = 'bg-yellow-200'
                  }
                }
              }

              // Disconnected radios get light red if no other status
              if (!rowBgClass && !radio.connected) {
                rowBgClass = 'bg-red-50/30'
              }

              return (
              <>
                <tr
                  key={radio.serial_number}
                  className={`hover:bg-gray-50 cursor-pointer ${rowBgClass}`}
                  onClick={() => toggleExpanded(radio.serial_number)}
                >
                  <td className="px-2 py-3">
                    {expandedRows.has(radio.serial_number) ? (
                      <ChevronDown className="h-4 w-4 text-gray-400" />
                    ) : (
                      <ChevronRight className="h-4 w-4 text-gray-400" />
                    )}
                  </td>
                  <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      checked={selectedRadios.includes(radio.serial_number)}
                      onChange={(e) =>
                        handleSelectRadio(radio.serial_number, e.target.checked)
                      }
                      className="rounded border-gray-300"
                    />
                  </td>
                  <td className="px-4 py-3 font-mono text-sm">
                    {radio.serial_number}
                    {radio.is_rogue && (
                      <span className="ml-2 px-1.5 py-0.5 text-xs font-semibold rounded bg-red-100 text-red-700">
                        ROGUE
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-sm">{radio.hostname || '-'}</td>
                  <td className="px-4 py-3 text-sm">
                    {(() => {
                      // Use customerCache if available (freshly fetched from Splynx)
                      const cached = customerCache.get(radio.serial_number)
                      if (cached?.customer_found && cached.customer_name) {
                        return cached.customer_name
                      }
                      // Fall back to radio data
                      return radio.customer_name || (
                        <span className="text-gray-400">Unassigned</span>
                      )
                    })()}
                  </td>
                  <td className="px-4 py-3 text-sm font-medium">
                    {(() => {
                      const cached = customerCache.get(radio.serial_number)
                      const slaMismatch = cached?.customer_found && cached.sla_profile &&
                                         radio.sla_profile !== cached.sla_profile
                      return (
                        <span className={slaMismatch ? 'text-red-600 font-semibold' : ''}>
                          {radio.sla_profile || '-'}
                        </span>
                      )
                    })()}
                  </td>
                  <td className="px-4 py-3">
                    <StatusBadge radio={radio} />
                  </td>
                  <td className="px-4 py-3 text-sm">
                    {radio.rssi !== null && radio.rssi !== undefined ? (
                      <span className={`font-mono ${
                        radio.rssi > -60 ? 'text-green-600' :
                        radio.rssi > -70 ? 'text-yellow-600' :
                        'text-red-600'
                      }`}>
                        {radio.rssi.toFixed(1)} dBm
                      </span>
                    ) : (
                      <span className="text-gray-400">-</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-sm font-mono">
                    {radio.distance !== null && radio.distance !== undefined ? (
                      `${(radio.distance / 1000).toFixed(2)} km`
                    ) : (
                      <span className="text-gray-400">-</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-sm font-mono">
                    {radio.pathloss !== null && radio.pathloss !== undefined ? (
                      `${radio.pathloss.toFixed(1)} dB`
                    ) : (
                      <span className="text-gray-400">-</span>
                    )}
                  </td>
                  <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                    <div className="flex space-x-2">
                      {hasRole(role, 'admin') && (
                        <button
                          onClick={() => handleSyncClick(radio.serial_number)}
                          disabled={syncMutation.isPending}
                          className="p-1 text-primary-600 hover:bg-primary-50 rounded"
                          title="Sync"
                        >
                          <RefreshCw
                            className={`h-4 w-4 ${syncMutation.isPending ? 'animate-spin' : ''}`}
                          />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
                {expandedRows.has(radio.serial_number) && (
                  <ExpandedRow
                    key={`${radio.serial_number}-expanded`}
                    radio={radio}
                    allRadios={data?.items}
                    setCustomerCache={setCustomerCache}
                  />
                )}
              </>
              )
            })}
          </tbody>
        </table>

        {/* Pagination */}
        <div className="bg-gray-50 px-4 py-3 flex items-center justify-between border-t border-gray-200">
          <div className="flex items-center space-x-2">
            <span className="text-sm text-gray-700">Show</span>
            <select
              value={pageSize}
              onChange={(e) => handlePageSizeChange(Number(e.target.value))}
              className="border border-gray-300 rounded-md text-sm py-1 px-2 focus:ring-2 focus:ring-primary-500"
            >
              {pageSizeOptions.map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
            <span className="text-sm text-gray-700">per page</span>
          </div>

          <div className="flex items-center space-x-2">
            <span className="text-sm text-gray-700">
              Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, totalItems)} of {totalItems}
            </span>
          </div>

          <div className="flex items-center space-x-1">
            <button
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="p-2 rounded-md hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>

            {getPageNumbers().map((page, idx) => (
              typeof page === 'number' ? (
                <button
                  key={idx}
                  onClick={() => setCurrentPage(page)}
                  className={`px-3 py-1 rounded-md text-sm ${
                    currentPage === page
                      ? 'bg-primary-600 text-white'
                      : 'hover:bg-gray-200'
                  }`}
                >
                  {page}
                </button>
              ) : (
                <span key={idx} className="px-2 text-gray-500">...</span>
              )
            ))}

            <button
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className="p-2 rounded-md hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Sync Confirmation Dialog */}
      <SyncConfirmDialog
        isOpen={syncDialogOpen}
        onClose={handleSyncCancel}
        onConfirm={handleSyncConfirm}
        preview={syncPreview}
        isLoading={syncPreviewLoading}
        isSyncing={syncMutation.isPending}
        syncError={syncError}
      />
    </div>
  )
}
